import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  Minus,
  Plus,
  ShoppingCart,
  ShieldCheck,
  Truck,
  PackageCheck,
} from "lucide-react";
import ProductCard from "@/components/ProductCard";
import ProductFeedback from "@/components/ProductFeedback";
import { RatingBadge } from "@/components/Stars";
import { FadeUp } from "@/components/Reveal";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";
import { formatBRL, categoryLabel } from "@/lib/format";

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQtyLocal] = useState(1);
  const [color, setColor] = useState("");
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null);
    setQtyLocal(1);
    setColor("");
    setMissing(false);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        setColor((r.data.colors || [])[0] || "");
      })
      .catch(() => setMissing(true));
  }, [id]);

  useEffect(() => {
    if (!product) return;
    api
      .get("/products", { params: { category: product.category } })
      .then((r) => setRelated(r.data.filter((p) => p.id !== product.id).slice(0, 4)))
      .catch(() => setRelated([]));
  }, [product]);

  if (missing) {
    return (
      <div className="bg-ink min-h-screen flex flex-col items-center justify-center gap-5 text-center px-6">
        <p className="font-display text-4xl">Produto não encontrado</p>
        <p className="text-mute">Ele pode ter sido vendido ou removido do catálogo.</p>
        <Link
          to="/loja"
          className="inline-flex h-12 items-center gap-2 rounded-md bg-cyan px-7 font-semibold text-ink"
          data-testid="product-missing-back"
        >
          Voltar ao catálogo
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="bg-ink min-h-screen flex items-center justify-center">
        <span className="h-8 w-8 rounded-full border-2 border-cyan border-t-transparent animate-spin" />
      </div>
    );
  }

  const soldOut = product.stock <= 0;

  return (
    <div className="bg-ink">
      <div className="mx-auto max-w-7xl px-5 lg:px-10 pt-28 md:pt-32 pb-16">
        <Link
          to="/loja"
          className="inline-flex items-center gap-2 text-sm text-mute hover:text-cyan transition-colors"
          data-testid="product-back-link"
        >
          <ArrowLeft size={16} />
          Catálogo
        </Link>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 mt-8 items-start">
          <FadeUp className="relative">
            <div
              className="relative rounded-lg overflow-hidden border border-white/10 bg-elevated"
              data-testid="product-detail-image"
            >
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(400px 300px at 50% 30%, rgba(0,229,255,0.14), transparent 70%)",
                }}
              />
              <img
                src={product.image_url}
                alt={product.name}
                className="relative w-full aspect-square object-cover"
              />
            </div>
            <div className="absolute -inset-2.5 border border-cyan/25 rounded-lg -z-10 rotate-1" />
          </FadeUp>

          <FadeUp delay={0.08}>
            <p className="text-xs uppercase tracking-[0.22em] text-cyan">
              {categoryLabel(product.category)}
              {product.subcategory && <span className="text-mute"> · {product.subcategory}</span>}
              {product.brand && <span className="text-mute"> — {product.brand}</span>}
            </p>
            <h1
              className="font-display font-semibold tracking-tight text-4xl md:text-5xl leading-[1.02] mt-4"
              data-testid="product-detail-title"
            >
              {product.name}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-6">
              <p
                className="font-display text-3xl md:text-4xl text-cyan"
                data-testid="product-detail-price"
              >
                {formatBRL(product.price)}
              </p>
              <RatingBadge
                value={product.rating_avg}
                count={product.rating_count}
                testid="product-detail-rating"
              />
              {product.name.includes("(Lacrado)") && (
                <span
                  className="inline-flex items-center gap-1.5 text-xs bg-emerald-400/10 border border-emerald-400/40 text-emerald-300 px-3 py-1.5 rounded-md"
                  data-testid="product-detail-warranty"
                >
                  <ShieldCheck size={14} />
                  Lacrado — Garantia de 1 ano
                </span>
              )}
            </div>
            <p className="text-mute leading-relaxed mt-5 max-w-lg">
              {product.description}
            </p>

            {product.colors?.length > 0 && (
              <div className="mt-7" data-testid="product-colors">
                <p className="text-sm text-mute mb-2.5">
                  Cores disponíveis: <span className="text-white font-medium">{color}</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((c) => (
                    <button
                      key={c}
                      onClick={() => setColor(c)}
                      className={`h-9 px-3.5 rounded-md text-xs border transition-all duration-300 ${
                        color === c
                          ? "bg-cyan/10 text-cyan border-cyan/50"
                          : "border-white/15 text-mute hover:text-white hover:border-white/40"
                      }`}
                      data-testid={`color-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {product.specs?.length > 0 && (
              <ul className="mt-7 space-y-3" data-testid="product-detail-specs">
                {product.specs.map((s) => (
                  <li key={s} className="flex items-center gap-3 text-sm">
                    <span className="h-5 w-5 rounded-full bg-cyan/10 border border-cyan/40 flex items-center justify-center shrink-0">
                      <Check size={12} className="text-cyan" />
                    </span>
                    {s}
                  </li>
                ))}
              </ul>
            )}

            <p
              className={`text-sm mt-7 ${soldOut ? "text-destructive" : "text-mute"}`}
              data-testid="product-detail-stock"
            >
              {soldOut
                ? "Produto esgotado"
                : `Em estoque: ${product.stock} ${product.stock === 1 ? "unidade" : "unidades"}`}
            </p>

            <div className="flex flex-wrap items-center gap-4 mt-6">
              <div className="flex items-center border border-white/15 rounded-md h-12">
                <button
                  onClick={() => setQtyLocal((q) => Math.max(1, q - 1))}
                  className="h-full px-4 text-mute hover:text-cyan transition-colors"
                  aria-label="Diminuir quantidade"
                  data-testid="qty-decrease"
                >
                  <Minus size={15} />
                </button>
                <span className="w-8 text-center" data-testid="qty-value">
                  {qty}
                </span>
                <button
                  onClick={() => setQtyLocal((q) => Math.min(10, q + 1))}
                  className="h-full px-4 text-mute hover:text-cyan transition-colors"
                  aria-label="Aumentar quantidade"
                  data-testid="qty-increase"
                >
                  <Plus size={15} />
                </button>
              </div>
              <button
                onClick={() => addItem(product, qty, color)}
                disabled={soldOut}
                className="inline-flex h-12 flex-1 min-w-[200px] items-center justify-center gap-2 rounded-md bg-cyan px-7 font-semibold text-ink transition-all duration-300 hover:bg-cyan-dark hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0"
                data-testid="add-to-cart-button"
              >
                <ShoppingCart size={18} />
                {soldOut ? "Indisponível" : "Adicionar ao carrinho"}
              </button>
            </div>

            <div className="flex flex-wrap gap-x-7 gap-y-3 mt-9 pt-7 border-t border-white/10 text-xs text-mute">
              <span className="flex items-center gap-2">
                <ShieldCheck size={14} className="text-cyan" /> Pagamento seguro via Mercado Pago
              </span>
              <span className="flex items-center gap-2">
                <Truck size={14} className="text-cyan" /> Frete grátis para todo o Brasil
              </span>
              <span className="flex items-center gap-2">
                <PackageCheck size={14} className="text-cyan" /> Estoque em tempo real
              </span>
            </div>
          </FadeUp>
        </div>

        <ProductFeedback product={product} />

        {related.length > 0 && (
          <section className="mt-24" data-testid="product-related">
            <h2 className="font-display font-semibold tracking-tight text-3xl md:text-4xl">
              Você também pode gostar
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
